import {
  Box,
  Table,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
  ButtonGroup,
  IconButton,
  Button,
  HStack,
  useToast,
  Select,
} from '@chakra-ui/react';
import { useState } from 'react';
import { RiCheckLine, RiLink, RiLoopLeftLine } from 'react-icons/ri';
import { ConfirmationModal } from '../../../../../../../components/ConfirmationModal';
import { useAuth } from '../../../../../../../hooks/auth';
import {
  IUserOnboardBase,
  OnboardTypeLabel,
} from '../../../../../../../models/users';
import { IVentureExperienceLevelBase } from '../../../../../../../models/ventureExperienceLevels';
import { IVentureBase } from '../../../../../../../models/ventures';
import { IFeatureGroupListItem } from '../../../../../../../services/FeatureGroups/ListFeatureGroupsService';
import setUsersOnboardingService from '../../../../../../../services/Users/SetUsersOnboardingService';

export type IFeatureGroup = IFeatureGroupListItem;

interface IOnboardTableVenture extends IVentureBase {
  experienceLevels: IVentureExperienceLevelBase[];
}

export interface IOnboardTableItem extends IUserOnboardBase {
  venture: IOnboardTableVenture;
}

interface IOnboardTableProps extends BoxProps {
  onboards: IOnboardTableItem[];
  showOnboardUrl?: boolean;
  onUpdateOnboard: (onboard: IOnboardTableItem) => void;
}

export const OnboardTable = ({
  onboards,
  showOnboardUrl = false,
  onUpdateOnboard,
  ...rest
}: IOnboardTableProps): JSX.Element => {
  const { experienceLevelsTitleMap } = useAuth();

  const toast = useToast();

  const [handlingOnboard, setHandlingOnboard] =
    useState<IOnboardTableItem | null>(null);
  const [isUpdateOnboardModalVisible, setIsUpdateOnboardModalVisible] =
    useState(false);

  const toggleUpdateOnboardModal = (
    onboard: IOnboardTableItem | null,
  ): void => {
    setHandlingOnboard(onboard);

    setIsUpdateOnboardModalVisible((prevState) => !prevState);
  };

  const handleCopyUrl = async (linkUrl: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(linkUrl);

      toast({
        description: 'O link foi copiado corretamente.',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
        status: 'success',
        title: 'Copiado com sucesso',
        variant: 'subtle',
      });
    } catch (err) {
      toast({
        description: 'Ocorreu um erro ao copiar o link, tente novamente.',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
        status: 'error',
        title: 'Falha ao copiar',
        variant: 'subtle',
      });
    }
  };

  const handleUpdateOnboarding = async ({
    experience,
    id,
    onboardType,
  }: IOnboardTableItem): Promise<void> => {
    const updatedOnboard = await setUsersOnboardingService({
      experience: onboardType === 'PARTIAL' ? 0 : experience,
      onboardId: id,
      onboardType,
    });

    onUpdateOnboard(updatedOnboard);

    toggleUpdateOnboardModal(null);
  };

  return (
    <Box {...rest}>
      {handlingOnboard && (
        <ConfirmationModal
          isOpen={isUpdateOnboardModalVisible}
          onClose={() => toggleUpdateOnboardModal(null)}
          onConfirm={() => handleUpdateOnboarding(handlingOnboard)}
          title={
            handlingOnboard.onboardType === 'PARTIAL'
              ? 'Reiniciar onboarding'
              : 'Finalizar onboarding'
          }
          message={
            handlingOnboard?.onboardType === 'PARTIAL'
              ? 'Deseja reiniciar o onboard desse usuário?'
              : `Deseja finalizar o onboard desse usuário com nível ${experienceLevelsTitleMap.get(
                  handlingOnboard.experience,
                )}?`
          }
        />
      )}

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th>Empreendimento</Th>
            <Th textAlign="center">Onboard status</Th>
            <Th>Nível de experiência</Th>
            {showOnboardUrl && <Th>Link onboard web</Th>}
            <Th textAlign="right" w="8" />
          </Tr>
        </Thead>

        <Tbody>
          {onboards.map((onboard) => (
            <Tr key={onboard.id}>
              <Td>
                <Text fontWeight="bold">{onboard.venture.name}</Text>
              </Td>

              <Td textAlign="center">
                {/* <OnboardStatusBadge onboardType={onboard.onboardType} /> */}
                {OnboardTypeLabel[onboard.onboardType]}
              </Td>

              <Td>
                <Select
                  borderColor="gray.300"
                  bg="gray.100"
                  focusBorderColor="blue.300"
                  variant="outline"
                  size="sm"
                  value={onboard.experience}
                  onChange={(e) =>
                    toggleUpdateOnboardModal({
                      ...onboard,
                      experience: Number(e.target.value),
                      onboardType: 'COMPLETED',
                    })
                  }
                >
                  {onboard.venture.experienceLevels.map((opt) => (
                    <option key={opt.id} value={opt.level}>
                      {opt.title}
                    </option>
                  ))}
                </Select>
              </Td>

              {showOnboardUrl && (
                <Td>
                  {onboard.onboardingUrl ? (
                    <HStack spacing="4">
                      <Button
                        size="sm"
                        leftIcon={<Icon as={RiLink} fontSize="20" />}
                        fontSize="sm"
                        colorScheme="blue"
                        onClick={() =>
                          handleCopyUrl(onboard.onboardingUrl || '')
                        }
                      >
                        Copiar
                      </Button>

                      <Text color="gray.500">{onboard.onboardingUrl}</Text>
                    </HStack>
                  ) : (
                    ''
                  )}
                </Td>
              )}

              <Td textAlign="right">
                <ButtonGroup>
                  {onboard.onboardType !== 'COMPLETED' && (
                    <IconButton
                      aria-label="finish-onboard"
                      size="sm"
                      icon={<Icon as={RiCheckLine} />}
                      fontSize="16"
                      color="white"
                      colorScheme="green"
                      onClick={() =>
                        toggleUpdateOnboardModal({
                          ...onboard,
                          onboardType: 'COMPLETED',
                        })
                      }
                    />
                  )}

                  <IconButton
                    aria-label="reset-onboard"
                    size="sm"
                    icon={<Icon as={RiLoopLeftLine} />}
                    fontSize="16"
                    colorScheme="red"
                    onClick={() =>
                      toggleUpdateOnboardModal({
                        ...onboard,
                        onboardType: 'PARTIAL',
                      })
                    }
                  />
                </ButtonGroup>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};
